/**
 * Mono downmix and the min/max peak pyramid (W2-08 timing; ARCHITECTURE.md §3.2 `core/audio-dsp/peaks.ts`).
 *
 * The pyramid (`PeakLevel`, core/audio-dsp/types.ts) has one level per block size 64, 256, 1024, 4096 and 16384
 * samples: entry i of a level holds the minimum and maximum sample of samples [i * block, (i + 1) * block) (the last
 * block may be shorter). Each level is derived from the previous one (4 blocks per block), so building the whole
 * pyramid reads the PCM once. Drawers pick the coarsest level whose block is no larger than the samples covered by
 * one pixel (`pickPeakLevel`) and reduce the blocks under a pixel with `peakRange`.
 */

import type { PeakLevel } from './types';

/** Block sizes of the pyramid (samples), finest first. */
export const PEAK_BLOCKS: readonly number[] = [64, 256, 1024, 4096, 16384];

/** Average of the channels (a single channel is returned as a copy). Shorter channels read as silence. */
export function downmixMono(channels: readonly Float32Array[]): Float32Array {
  const first = channels[0];
  if (first === undefined) return new Float32Array(0);
  let length = 0;
  for (const ch of channels) length = Math.max(length, ch.length);
  const out = new Float32Array(length);
  if (channels.length === 1) {
    out.set(first);
    return out;
  }
  const scale = 1 / channels.length;
  for (const ch of channels) {
    const n = ch.length;
    for (let i = 0; i < n; i++) out[i] = (out[i] ?? 0) + (ch[i] ?? 0) * scale;
  }
  return out;
}

function sanitize(x: number): number {
  return Number.isFinite(x) ? x : 0;
}

/** First level straight from the PCM. */
function baseLevel(mono: Float32Array, block: number): PeakLevel {
  const count = Math.ceil(mono.length / block);
  const min = new Float32Array(count);
  const max = new Float32Array(count);
  for (let b = 0; b < count; b++) {
    const start = b * block;
    const end = Math.min(mono.length, start + block);
    let lo = Infinity;
    let hi = -Infinity;
    for (let i = start; i < end; i++) {
      const v = sanitize(mono[i] ?? 0);
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    min[b] = lo;
    max[b] = hi;
  }
  return { block, min, max };
}

/** A coarser level from a finer one (`block` must be a multiple of `prev.block`). */
function coarserLevel(prev: PeakLevel, block: number): PeakLevel {
  const ratio = block / prev.block;
  const count = Math.ceil(prev.min.length / ratio);
  const min = new Float32Array(count);
  const max = new Float32Array(count);
  for (let b = 0; b < count; b++) {
    const start = b * ratio;
    const end = Math.min(prev.min.length, start + ratio);
    let lo = Infinity;
    let hi = -Infinity;
    for (let i = start; i < end; i++) {
      const a = prev.min[i] ?? 0;
      const c = prev.max[i] ?? 0;
      if (a < lo) lo = a;
      if (c > hi) hi = c;
    }
    min[b] = lo;
    max[b] = hi;
  }
  return { block, min, max };
}

/** The pyramid of `mono` (one level per block size, finest first). Empty input gives empty levels. */
export function computePeaks(mono: Float32Array, blocks: readonly number[] = PEAK_BLOCKS): PeakLevel[] {
  const sorted = [...blocks].sort((a, b) => a - b);
  const levels: PeakLevel[] = [];
  for (const block of sorted) {
    if (!(Number.isInteger(block) && block > 0)) throw new RangeError(`computePeaks: bad block size ${String(block)}`);
    const prev = levels[levels.length - 1];
    levels.push(prev !== undefined && block % prev.block === 0 ? coarserLevel(prev, block) : baseLevel(mono, block));
  }
  return levels;
}

/** Coarsest level whose block is at most `samplesPerPixel` (the finest level when pixels are smaller than a block). */
export function pickPeakLevel(levels: readonly PeakLevel[], samplesPerPixel: number): PeakLevel | null {
  let best: PeakLevel | null = null;
  for (const l of levels) {
    if (l.block <= samplesPerPixel && (best === null || l.block > best.block)) best = l;
  }
  if (best !== null) return best;
  for (const l of levels) if (best === null || l.block < best.block) best = l;
  return best;
}

/**
 * Min and max of the samples [fromSample, toSample) as seen by `level` (blocks overlapping the range), or null when
 * the range holds no block.
 */
export function peakRange(
  level: PeakLevel,
  fromSample: number,
  toSample: number,
): { readonly min: number; readonly max: number } | null {
  const count = level.min.length;
  const b0 = Math.max(0, Math.floor(fromSample / level.block));
  const b1 = Math.min(count, Math.max(b0 + 1, Math.ceil(toSample / level.block)));
  if (b0 >= count || b1 <= 0 || !(toSample > fromSample)) return null;
  let lo = Infinity;
  let hi = -Infinity;
  for (let b = b0; b < b1; b++) {
    const a = level.min[b] ?? 0;
    const c = level.max[b] ?? 0;
    if (a < lo) lo = a;
    if (c > hi) hi = c;
  }
  return lo <= hi ? { min: lo, max: hi } : null;
}

/** Number of samples the pyramid covers (from its finest level). */
export function peakSampleCount(levels: readonly PeakLevel[]): number {
  let best: PeakLevel | null = null;
  for (const l of levels) if (best === null || l.block < best.block) best = l;
  return best === null ? 0 : best.min.length * best.block;
}
